/**
 * Tiny router. The app has 4 routes:
 *   /                       → BoardsList
 *   /boards/:id             → BoardView (renders the current phase)
 *   /share/compose          → ShareCompose (from Web Share Target)
 *   *                       → NotFound
 *
 * No need for react-router for four routes; the History API does just fine
 * and we avoid a dependency.
 */

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

interface RouterCtx {
  pathname: string;
  navigate: (to: string, replace?: boolean) => void;
}

const Ctx = createContext<RouterCtx | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = (to: string, replace = false) => {
    if (replace) window.history.replaceState({}, '', to);
    else window.history.pushState({}, '', to);
    setPathname(to);
  };

  return <Ctx.Provider value={{ pathname, navigate }}>{children}</Ctx.Provider>;
}

export function useRouter(): RouterCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRouter outside RouterProvider');
  return v;
}

export function Link(props: {
  to: string;
  className?: string;
  children: ReactNode;
  replace?: boolean;
  onClick?: () => void;
}) {
  const { navigate } = useRouter();
  return (
    <a
      href={props.to}
      className={props.className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        props.onClick?.();
        navigate(props.to, props.replace);
      }}
    >
      {props.children}
    </a>
  );
}

/** Match a path like "/boards/:id". Returns params, or null if no match. */
export function match(
  pattern: string,
  pathname: string,
): Record<string, string> | null {
  const pSeg = pattern.split('/').filter(Boolean);
  const aSeg = pathname.split('/').filter(Boolean);
  if (pSeg.length !== aSeg.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < pSeg.length; i++) {
    const p = pSeg[i]!;
    const a = aSeg[i]!;
    if (p.startsWith(':')) out[p.slice(1)] = decodeURIComponent(a);
    else if (p !== a) return null;
  }
  return out;
}
